import { API_BASE } from '@/config/api'
import router from '@/router'
import { clearUser, getUser } from '@/utils/auth'
import message from '@/utils/message'

export const AUTH_EXPIRED_STATUS = -100

let authRedirecting = false

/** 业务层返回的未登录 / 会话失效 */
export function isAuthExpiredResponse (data) {
  if (!data || typeof data !== 'object') return false
  if (Number(data.status) === AUTH_EXPIRED_STATUS) return true
  const msg = String(data.msg || '')
  return /未登录|登录已过期|登录失效/.test(msg)
}

export function isUnauthorizedError (err) {
  return !!(err && err.code === 'UNAUTHORIZED')
}

function shouldSkipAuthRedirect (path) {
  return /\/user\/login$/i.test(String(path || ''))
}

function redirectToLogin (tip) {
  if (authRedirecting) return
  authRedirecting = true
  clearUser()
  message.warning(tip || '登录已过期，请重新登录')
  const current = router.currentRoute
  const goLogin =
    !current || current.name !== 'Login'
      ? router.replace({ name: 'Login' })
      : Promise.resolve()
  Promise.resolve(goLogin).finally(() => {
    window.setTimeout(() => {
      authRedirecting = false
    }, 600)
  })
}

function buildAuthHeaders () {
  const user = getUser()
  const headers = {}
  if (user && user.token) headers.token = String(user.token)
  if (user && user.userId != null) headers.userId = String(user.userId)
  return headers
}

function buildUrl (path, query) {
  const base = `${API_BASE}${path}`
  if (!query) return base
  const qs = Object.keys(query)
    .filter(key => query[key] != null && query[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
    .join('&')
  return qs ? `${base}?${qs}` : base
}

/**
 * @param {string} path 以 / 开头的接口路径，如 /zhima/user/login
 * @param {RequestInit & { body?: object, query?: object, timeout?: number }} options
 */
export function request (path, options = {}) {
  const { body, query, method = 'GET', headers, timeout = 30000, ...rest } = options
  const url = buildUrl(path, query)
  const isFormData = body instanceof FormData
  const hasBody = body !== undefined && method !== 'GET'
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer = null

  const fetchPromise = fetch(url, {
    method,
    credentials: 'include',
    ...rest,
    signal: controller ? controller.signal : undefined,
    headers: {
      ...buildAuthHeaders(),
      ...(hasBody && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    },
    body: hasBody ? (isFormData ? body : JSON.stringify(body)) : undefined
  })

  const timedPromise = new Promise((resolve, reject) => {
    if (controller && timeout > 0) {
      timer = setTimeout(() => {
        controller.abort()
        const err = new Error('请求超时，请稍后重试')
        err.code = 'TIMEOUT'
        reject(err)
      }, timeout)
    }
    fetchPromise.then(resolve, reject)
  })

  return timedPromise.then(async (res) => {
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      if (isAuthExpiredResponse(data) && !shouldSkipAuthRedirect(path)) {
        redirectToLogin(data.msg)
        const err = new Error(data.msg || '用户未登录')
        err.code = 'UNAUTHORIZED'
        err.response = data
        err.status = data.status != null ? data.status : res.status
        throw err
      }
      const err = new Error(data.msg || `请求失败 (${res.status})`)
      err.response = data
      err.status = res.status
      throw err
    }
    if (isAuthExpiredResponse(data) && !shouldSkipAuthRedirect(path)) {
      redirectToLogin(data.msg)
      const err = new Error(data.msg || '用户未登录')
      err.code = 'UNAUTHORIZED'
      err.response = data
      err.status = data.status
      throw err
    }
    return data
  }).catch((err) => {
    if (err && err.name === 'AbortError') {
      const timeoutErr = new Error('请求超时，请稍后重试')
      timeoutErr.code = 'TIMEOUT'
      throw timeoutErr
    }
    throw err
  }).finally(() => {
    if (timer) clearTimeout(timer)
  })
}



// WEBPACK FOOTER //
// ./src/utils/request.js